import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useContext } from 'react';
import Chatbot from '../chatbot';
import ModalModule from '../modal';
import UI from '../ui';
import Utils from '../utils';
import { useIdentityVerification } from './hooks';

const { WrappedButtonType, InlineMessage } = UI;
const { Analytics } = Utils;

const { ChatbotTriggers, launchChatbot } = Chatbot;
const { Modal, ModalButtonActionType, ModalContext } = ModalModule;

const usCurrencyFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
});

type TransactionCompleteModalProps = {
	modalId: number;
	data: {
		purchaseAmount: number;
		remainingFundingLimit: number;
	};
};

export const TransactionCompleteModal = ({
	modalId,
	data: { purchaseAmount, remainingFundingLimit },
}: TransactionCompleteModalProps) => {
	const { closeModal, showModal } = useContext(ModalContext);
	const { startVerification } = useIdentityVerification();
	const flags = useFlags();

	const isPurchaseLimitReached = remainingFundingLimit <= 0;
	const startChat = () => {
		closeModal(modalId, ModalButtonActionType.Ok);

		Analytics.trackEvent(
			'live-chat-transfer',
			{
				limit_remaining: remainingFundingLimit,
			},
			Analytics.agent.segment
		);

		launchChatbot({ flags, showModal, trigger: ChatbotTriggers.AddFunds });
	};

	const primaryButton = {
		type: ModalButtonActionType.Ok,
		text: 'Verify now',
		onClick: () => {
			closeModal(modalId, ModalButtonActionType.Ok);

			const trackVerificationOutcome = (
				limitRemaining: number,
				outcome: 'success' | 'failure' | 'aborted'
			) => {
				Analytics.trackEvent(
					'plaid-verification-attempt',
					{
						limit_remaining: limitRemaining,
						outcome,
					},
					Analytics.agent.segment
				);
			};

			startVerification({
				onSuccess: () =>
					trackVerificationOutcome(remainingFundingLimit, 'success'),
				onFailure: () =>
					trackVerificationOutcome(remainingFundingLimit, 'failure'),
				onCancel: () =>
					trackVerificationOutcome(remainingFundingLimit, 'aborted'),
			});
		},
		dataqa: 'transaction-complete-verify-now-button',
	};

	const secondaryButton = {
		type: ModalButtonActionType.Ok,
		buttonType: WrappedButtonType.TertiaryWhite,
		onClick: startChat,
		text: 'Start chat',
		dataqa: 'transaction-complete-start-chat-button',
	};

	return (
		<Modal
			modalId={modalId}
			header="Transaction Complete"
			primaryButton={primaryButton}
			secondaryButton={secondaryButton}
			size="small"
		>
			<p className="transaction-complete-message">
				Your fund request for {usCurrencyFormatter.format(purchaseAmount)} has
				been approved.
			</p>
			<InlineMessage
				type={isPurchaseLimitReached ? 'warning' : 'information'}
				header={
					isPurchaseLimitReached
						? `You've reached your account funding limit.`
						: `${usCurrencyFormatter.format(remainingFundingLimit)} until you reach your funding limit.`
				}
				text="To remove this limit, you can verify your identity using our verification tool or chat with a live agent (available Monday–Friday 8am - 8pm CT)."
				dataqa="transaction-confirmation-inline-message"
			/>
		</Modal>
	);
};
